import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search, Users } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";
import {
    createAffiliate,
    getAffiliateDashboard,
    listAffiliates,
    type AffiliateDashboardDto,
    type AffiliateDto,
    type CreateAffiliateRequest,
} from "@/api/affiliates";

type StatusFilter = "all" | "active" | "archived";

const STATUS_VARIANT: Record<string, "success" | "muted" | "warning" | "default"> = {
    active: "success",
    archived: "muted",
};

export function AffiliatesListPage(): React.ReactNode {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [createOpen, setCreateOpen] = React.useState(false);
    const [searchInput, setSearchInput] = React.useState("");
    const [search, setSearch] = React.useState("");
    React.useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 200);
        return () => clearTimeout(t);
    }, [searchInput]);
    const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");

    const listQuery = useQuery({
        queryKey: ["affiliates", "list"],
        queryFn: () => listAffiliates(true),
    });

    const rows: AffiliateDto[] = React.useMemo(() => {
        const all = listQuery.data ?? [];
        return all.filter((a) => {
            if (statusFilter !== "all" && a.status !== statusFilter) return false;
            if (search) {
                const hay = `${a.slug} ${a.name} ${a.contactEmail ?? ""} ${a.payoutEmail ?? ""}`.toLowerCase();
                if (!hay.includes(search)) return false;
            }
            return true;
        });
    }, [listQuery.data, search, statusFilter]);

    const dashboardResults = useQueries({
        queries: rows.map((a) => ({
            queryKey: ["affiliate", a.id, "dashboard"],
            queryFn: () => getAffiliateDashboard(a.id),
            staleTime: 30_000,
        })),
    });
    const dashboardById = React.useMemo(() => {
        const m = new Map<number, AffiliateDashboardDto | undefined>();
        rows.forEach((a, i) => m.set(a.id, dashboardResults[i]?.data));
        return m;
    }, [rows, dashboardResults]);

    return (
        <>
            <PageHeader
                title="Affiliates"
                description="Traffic-side counterparties: who delivered the click/lead and who we pay commission to."
                actions={
                    <Button data-testid="new-affiliate-button" onClick={() => setCreateOpen(true)}>
                        <Plus className="h-4 w-4" />
                        New affiliate
                    </Button>
                }
            />
            <div className="space-y-3 px-6 py-4">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 min-w-[260px]">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by slug, name, contact or payout email…"
                            className="h-7 border-0 bg-transparent px-0 focus-visible:ring-0"
                            data-testid="affiliates-search"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                        <SelectTrigger className="h-9 w-[160px]" data-testid="affiliates-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {listQuery.isLoading ? (
                    <div className="flex justify-center py-16"><Spinner size="lg" /></div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        icon={<Users className="h-8 w-8" />}
                        title={search || statusFilter !== "all" ? "No affiliates match those filters" : "No affiliates yet"}
                        description={search || statusFilter !== "all" ? "Try a different filter." : "Create one to start tracking commission and click attribution."}
                        action={!search && statusFilter === "all" ? (
                            <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> New affiliate</Button>
                        ) : undefined}
                    />
                ) : (
                    <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full text-sm" data-testid="affiliates-table">
                            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left">Slug</th>
                                    <th className="px-3 py-2 text-left">Name</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                    <th className="px-3 py-2 text-right">Campaigns</th>
                                    <th className="px-3 py-2 text-right">Clicks</th>
                                    <th className="px-3 py-2 text-right">Leads</th>
                                    <th className="px-3 py-2 text-right">CR&nbsp;%</th>
                                    <th className="px-3 py-2 text-left">Commission</th>
                                    <th className="px-3 py-2 text-left">Updated</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((a) => {
                                    const d = dashboardById.get(a.id);
                                    const variant = STATUS_VARIANT[a.status] ?? "default";
                                    return (
                                        <tr
                                            key={a.id}
                                            onClick={() => navigate(`/affiliates/${a.id}`)}
                                            className={cn("cursor-pointer hover:bg-muted/30")}
                                            data-testid={`affiliate-row-${a.id}`}
                                        >
                                            <td className="px-3 py-2">
                                                <Badge variant="outline" className="font-mono text-[11px]">{a.slug}</Badge>
                                            </td>
                                            <td className="px-3 py-2 font-medium">{a.name}</td>
                                            <td className="px-3 py-2"><Badge variant={variant}>{a.status}</Badge></td>
                                            <td className="px-3 py-2 text-right tabular-nums">{d?.campaignsOwned ?? "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{d?.clicksAllTime ?? "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{d?.leadsAllTime ?? "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{formatPct(d?.conversionRatePct)}</td>
                                            <td className="px-3 py-2 text-muted-foreground">{formatBps(a.commissionBps)}</td>
                                            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(a.updatedAt)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <CreateAffiliateDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={(aff) => {
                    queryClient.invalidateQueries({ queryKey: ["affiliates", "list"] });
                    navigate(`/affiliates/${aff.id}`);
                }}
            />
        </>
    );
}

function formatPct(value: number | null | undefined): string {
    if (value == null) return "—";
    return `${value.toFixed(2)}%`;
}

function formatBps(bps: number | null | undefined): string {
    if (bps == null) return "—";
    return `${(bps / 100).toFixed(2)}%`;
}

function CreateAffiliateDialog({
    open,
    onOpenChange,
    onCreated,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCreated: (aff: AffiliateDto) => void;
}): React.ReactNode {
    const [slug, setSlug] = React.useState("");
    const [name, setName] = React.useState("");
    const [contactEmail, setContactEmail] = React.useState("");
    const [payoutEmail, setPayoutEmail] = React.useState("");
    const [commissionPct, setCommissionPct] = React.useState("");
    const [payoutTerms, setPayoutTerms] = React.useState("");
    const [notes, setNotes] = React.useState("");

    React.useEffect(() => {
        if (open) {
            setSlug(""); setName(""); setContactEmail(""); setPayoutEmail("");
            setCommissionPct(""); setPayoutTerms(""); setNotes("");
        }
    }, [open]);

    const createMut = useMutation({
        mutationFn: (req: CreateAffiliateRequest) => createAffiliate(req),
        onSuccess: (aff) => {
            toast.success(`Affiliate "${aff.name}" created`);
            onOpenChange(false);
            onCreated(aff);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't create affiliate")),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="affiliate-create-dialog">
                <DialogHeader>
                    <DialogTitle>New affiliate</DialogTitle>
                    <DialogDescription>
                        The slug is the stable identifier used by attribution. Commission is stored as basis points (50 bps = 0.50%).
                    </DialogDescription>
                </DialogHeader>
                <form
                    className="space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        const pctNum = commissionPct.trim() === "" ? null : Number.parseFloat(commissionPct);
                        const bps = pctNum != null && Number.isFinite(pctNum) ? Math.round(pctNum * 100) : null;
                        createMut.mutate({
                            slug: slug.trim(),
                            name: name.trim(),
                            contactEmail: contactEmail.trim() || null,
                            payoutEmail: payoutEmail.trim() || null,
                            commissionBps: bps,
                            payoutTerms: payoutTerms.trim() || null,
                            notes: notes.trim() || null,
                        });
                    }}
                >
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-slug">Slug</Label>
                            <Input
                                id="aff-slug"
                                data-testid="affiliate-slug-input"
                                value={slug}
                                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                                placeholder="bobs-traffic"
                                required
                                autoFocus
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-name">Name</Label>
                            <Input
                                id="aff-name"
                                data-testid="affiliate-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Bob's Traffic Co."
                                required
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-contact">Contact email</Label>
                            <Input id="aff-contact" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="bob@example.com" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-payout">Payout email</Label>
                            <Input id="aff-payout" type="email" value={payoutEmail} onChange={(e) => setPayoutEmail(e.target.value)} placeholder="payouts@example.com" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-comm">Commission %</Label>
                            <Input
                                id="aff-comm"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={commissionPct}
                                onChange={(e) => setCommissionPct(e.target.value)}
                                placeholder="e.g. 10"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-terms">Payout terms</Label>
                            <Input id="aff-terms" value={payoutTerms} onChange={(e) => setPayoutTerms(e.target.value)} placeholder="net-30 / weekly / etc." />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="aff-notes">Notes</Label>
                        <Textarea
                            id="aff-notes"
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Internal notes — primary contact, IM handle, etc."
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={createMut.isPending}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="affiliate-create-submit"
                            disabled={!slug.trim() || !name.trim() || createMut.isPending}
                        >
                            {createMut.isPending ? <Spinner size="sm" /> : "Create affiliate"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
